import { LearningMenuType } from "@/components/Learning/LearningMiddle/config/LearningMenuType";
import type { LearningStoreState } from "@/store/learning/state";
import type { LearningStoreGetters } from "@/store/learning/getters";
import type { LearningStoreActions } from "@/store/learning/actions";
import { isEmptyString } from "~/utils/StringUtil";
import { TaskType } from "~~/package/mapper/learning/model/TaskType";
import { LangType } from "~~/package/mapper/learning/model/LangType";
import { LearningTokenData } from "~~/package/mapper/learning/model/LearningTokenData";
import type TaskCompleteResponse from "~~/package/apis/learning/response/TaskCompleteResponse";
import type { TaskModel } from "~~/package/mapper/learning/model/TaskModel";
import type { LearningCourseModel } from "~~/package/mapper/learning/model/LearningCourseModel";
import type { LearningSkillModel } from "~~/package/mapper/learning/model/LearningSkillModel";
import { LearningTaskModel } from "~~/package/mapper/learning/model/LearningTaskModel";
import { createLinuxLearningState } from "@/store/learning/state/factories";

export const basicActions = {
    changeThemeMode(): void {
        (this as LearningStoreState).isDarkMode = !(this as LearningStoreState).isDarkMode;
    },
    resetInitData(): void {
        (this as LearningStoreState).isInitData = false;
    },
    resetSubmissionFlags(taskId = (this as LearningStoreState).task.id, preserveCurrentChanges = false): void {
        const state = this as LearningStoreState;
        const isCurrentTask = state.task.id === taskId;
        if (isCurrentTask && !preserveCurrentChanges) {
            state.isChangedButNotSubmit = false;
            state.isEmbedContentChanged = false;
        }
        state.unsubmittedChangeByTaskId[taskId] = preserveCurrentChanges;
    },
    updateMenuType(menuType: LearningMenuType, isAutoClose = true): void {
        if ((this as LearningStoreState).menuType === menuType && isAutoClose) (this as LearningStoreState).menuType = LearningMenuType.None;
        else (this as LearningStoreState).menuType = menuType;
    },
    updateCode(code: string): void {
        (this as LearningStoreState).currentCode = code;
    },
    updateCourse(course: LearningCourseModel): void {
        (this as LearningStoreState).course = course;
    },
    updateLearningData(task: TaskModel): void {
        (this as LearningStoreState).tokenData = new LearningTokenData();
        (this as LearningStoreState).isInitData = true;
        (this as LearningStoreState).currentCode = task.submitCode ? task.submitCode : task.defaultCode;
        (this as LearningStoreState).menuType = isEmptyString(task.description) ? LearningMenuType.None : LearningMenuType.Description;
        (this as LearningStoreState).isRunning = false;
        (this as LearningStoreState).codeOutPut = [];
        (this as LearningStoreState).errorOutPut = [];
        (this as LearningStoreState).isShowOutputDescription = false;
        (this as LearningStoreState).outputDescription = "";
        (this as LearningStoreState).errorGuide = "";
        (this as LearningStoreState).learningMessage = null;
        (this as LearningStoreState).correctCode = "";
        (this as LearningStoreState).imageList = [];
        (this as LearningStoreState).isShowImage = false;
        (this as LearningStoreState).isShowActivityMenu = false;
        (this as LearningStoreState).validSet = "";
        (this as LearningStoreState).score = undefined;
        (this as LearningStoreState).linuxLearning = createLinuxLearningState();

        task.markOrderList.sort((orderA, orderB) => orderA.order - orderB.order);
        task.markDictationList = task.markDictationList.map(dictation => ({
            ...dictation,
            value: dictation.userSubmitCode !== undefined ? dictation.userSubmitCode : ""
        }));
        task.markSelectionList = task.markSelectionList.map((selection) => ({
            ...selection,
            selectedIndex: selection.userSubmitCode !== undefined ? selection.userSubmitCode : -1
        }));
        task.markOrderList = task.markOrderList.map((order) => ({
            ...order,
            answerIndex: order.userSubmitCode !== undefined ? order.userSubmitCode : -1
        }));

        (this as LearningStoreState).task = task;
        (this as LearningStoreState).course.recentTaskId = task.id;

        (this as LearningStoreState).isOrderMenuOpen = false;
        (this as LearningStoreState).editorKey = (this as LearningStoreState).editorKey + 1;

        (this as LearningStoreState).taskComplete = null;
        (this as LearningStoreState).taskAnswer = null;
        (this as LearningStoreState).isLastTask = false;
        (this as LearningStoreState).menuActiveList = (this as LearningStoreGetters).isFrontendLearning ? [LearningMenuType.Preview] : [];
        
        (this as LearningStoreState).descriptiveAnswer = task.submitCode ?? "";


        if (task.type === TaskType.CONTENT && task.langType === LangType.AWS)
            this.updateMenuType(LearningMenuType.Description);
    },
    updateToken(tokenData: LearningTokenData): void {
        (this as LearningStoreState).tokenData = tokenData;
    },
    updateProgress(): void {
        const learning = (this as (LearningStoreGetters & LearningStoreState & LearningStoreActions));
        const task = learning.taskList.find(task => task.id === learning.task.id) as LearningTaskModel;
        task.completeFlag = true;

        learning.skillList.forEach((skill: LearningSkillModel) => {
            skill.completeFlag = skill.taskList.filter(task => task.completeFlag).length === skill.taskList.length;
        });

        learning.course.chapterList.forEach(chapter => {
            const chapterTaskList = chapter.skillList.map(skill => skill).flat();
            const completeChapterTaskLength = chapterTaskList.filter(task => task.completeFlag).length;
            const totalChapterTaskLength = chapterTaskList.length;
            chapter.progress = completeChapterTaskLength / totalChapterTaskLength * 100;
            chapter.completeFlag = completeChapterTaskLength === totalChapterTaskLength;
        });

        const completeTaskLength = learning.taskList.filter(task => task.completeFlag).length;
        const totalTaskLength = learning.taskList.length;
        learning.course.progress = completeTaskLength / totalTaskLength * 100;
    },
    runTraining(isPreview = false): void {
        (this as LearningStoreState).menuType = isPreview ? LearningMenuType.Preview : LearningMenuType.CodeOutPut;
        (this as LearningStoreState).menuActiveList = (this as LearningStoreGetters).isFrontendLearning ? [LearningMenuType.Preview] : [];
        (this as LearningStoreState).codeOutPut = [];
        (this as LearningStoreState).imageList = [];
        (this as LearningStoreState).isShowImage = false;
        (this as LearningStoreState).errorOutPut = [];
        (this as LearningStoreState).outputDescription = "";
        (this as LearningStoreState).errorGuide = "";
        (this as LearningStoreState).isRunning = true;
        (this as LearningStoreState).validSet = "";
        (this as LearningStoreState).score = undefined;
        (this as LearningStoreState).frontLearning.defaultLocation = "";
        (this as LearningStoreState).frontLearning.currentLocation = "";
    },
    addTrainingData(codeOutPut: string, imageDataList: string[], errorOutPut: string, errorGuide: string, isEnd: boolean, validSet: string, score: number | undefined): void {
        if (codeOutPut) {
            (this as LearningStoreState).codeOutPut.push(codeOutPut);
        }
        if (imageDataList && imageDataList.length > 0) {
            (this as LearningStoreState).imageList = [
                ...(this as LearningStoreState).imageList,
                ...imageDataList
            ];
        }
        if (errorOutPut) {
            (this as LearningStoreState).errorOutPut.push(errorOutPut);
        }
        if (errorGuide) {
            (this as LearningStoreState).errorGuide = errorGuide;
            (this as LearningStoreState).isShowOutputDescription = true;
        }
        if (isEnd) {
            (this as LearningStoreState).isShowImage = (this as LearningStoreState).imageList.length > 0;
            (this as LearningStoreState).isRunning = false;
        }
        if (!(this as LearningStoreState).validSet && validSet) {
            (this as LearningStoreState).validSet = validSet;
        }
        if (!(this as LearningStoreState).score && score) {
            (this as LearningStoreState).score = score;
        }
    },
    updateIsShowImage(isShow: boolean): void {
        (this as LearningStoreState).isShowImage = isShow;
    },
    resetCodeData(): void {
        if ((this as LearningStoreState).currentCode !== (this as LearningStoreState).task.defaultCode) {
            (this as LearningStoreState).isChangedButNotSubmit = true;
        }

        (this as LearningStoreState).currentCode = (this as LearningStoreState).task.defaultCode;

        (this as LearningStoreState).task.markSelectionList.forEach(selection => {
            selection.selectedIndex = -1;
        });
        (this as LearningStoreState).task.markDictationList.forEach(input => {
            input.value = "";
        });
        (this as LearningStoreState).task.markOrderList.forEach(order => {
            order.answerIndex = -1;
        });
        (this as LearningStoreState).task.submitCode = "";

        (this as LearningStoreState).editorKey = (this as LearningStoreState).editorKey + 1;
        (this as LearningStoreState).isRunning = false;
        (this as LearningStoreState).learningMessage = null;

        (this as LearningStoreState).taskComplete = null;
        (this as LearningStoreState).taskAnswer = null;

        (this as LearningStoreState).isShowActivityMenu = false;
        (this as LearningStoreState).imageList = [];
        (this as LearningStoreState).isShowImage = false;

        (this as LearningStoreState).isUpdateCode = true;

        (this as LearningStoreActions).resetMultiFileLearning();
        (this as LearningStoreActions).clearCustomFunctions();

        (this as LearningStoreState).isHideBarsToggle = false;
    },
    handlingTaskSuccess(): void {
        (this as LearningStoreState).task.inCorrectAnswerCodeCount += 1;
        (this as LearningStoreState).learningMessage = false;
    },
    resetLearningMessage(): void {
        (this as LearningStoreState).learningMessage = null;
    },
    handleTaskComplete(taskCompleteData: TaskCompleteResponse) {
        if (![TaskType.DESCRIPTIVE_QA, TaskType.INSTRUCTION_DESCRIPTIVE_QA].includes((this as LearningStoreState).task.type))
            (this as LearningStoreState).taskAnswer = null;

        (this as LearningStoreState).taskComplete = taskCompleteData;
        if (taskCompleteData.correctFlag) {
            (this as LearningStoreActions).updateProgress();
            (this as LearningStoreState).outputDescription = taskCompleteData.outputDescription;
        } else {
            (this as LearningStoreActions).handlingTaskSuccess();
        }
        (this as LearningStoreState).learningMessage = taskCompleteData.correctFlag;
    },
    handleTaskValidate(taskCompleteData: TaskCompleteResponse) {
        (this as LearningStoreState).learningMessage = taskCompleteData.correctFlag;
    },
    refreshPreview() {
        (this as LearningStoreState).frontLearning.iframeEnable = false;
        setTimeout(() => {
            (this as LearningStoreState).frontLearning.iframeEnable = true;
        }, 10);
    },
    updateSplitPaneSize(leftSize: number, rightSize: number): void {
        (this as LearningStoreState).splitPane.leftSize = leftSize;
        (this as LearningStoreState).splitPane.rightSize = rightSize;
    }
};
